﻿import { HardDrive, Network, RefreshCw, Server, Users } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Button, PageHeader, Skeleton, StatCard, TabNav } from '@/components/ui';
import { useNotify } from '@/hooks/useNotify';
import { useT } from '@/i18n';
import { api, type AdminServer, type AdminUser, type AuditEntry, type Instance } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { paths } from '@/router/paths';

interface AdminData {
  servers: AdminServer[];
  instances: Instance[];
  users: AdminUser[];
  audit: AuditEntry[];
  loading: boolean;
  reload: () => Promise<void>;
}

const AdminContext = createContext<AdminData | null>(null);

/** Gemeinsame Daten aller Administrationsseiten. */
export function useAdminData(): AdminData {
  const context = useContext(AdminContext);
  if (!context) throw new Error('useAdminData nur innerhalb des Administrationsbereichs');
  return context;
}

export function AdminLayout() {
  const notify = useNotify();
  const t = useT();
  const [servers, setServers] = useState<AdminServer[]>([]);
  const [instances, setInstances] = useState<Instance[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [serverData, instanceData, userData, auditData] = await Promise.all([
        api.admin.servers(),
        api.admin.instances(),
        api.admin.users(),
        api.admin.audit(150),
      ]);
      setServers(serverData.servers);
      setInstances(instanceData.instances);
      setUsers(userData.users);
      setAudit(auditData.entries);
    } catch (cause) {
      notify.fromError(cause, 'Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void reload();
    // absichtlich nur beim ersten Rendern
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const result = await api.admin.reconcile();
      notify.success(t('admin.reconciled', { restored: result.restored, failed: result.failed }));
      await reload();
    } catch (cause) {
      notify.fromError(cause, 'Abgleich fehlgeschlagen');
    } finally {
      setReconciling(false);
    }
  };

  const value = useMemo<AdminData>(
    () => ({ servers, instances, users, audit, loading, reload }),
    [servers, instances, users, audit, loading, reload],
  );

  const totalHostIps = servers.reduce(
    (sum, server) => sum + server.publicIpPools.reduce((inner, pool) => inner + pool.addressCount, 0),
    0,
  );
  // Die genaue Zahl freier Adressen liefert die IPAM-Ansicht; hier genuegt der Pool-Umfang
  const freeHostIps = totalHostIps - instances.filter((instance) => instance.isDedicated).length;

  return (
    <AdminContext.Provider value={value}>
      <PageHeader
        title={t('admin.title')}
        subtitle={t('admin.subtitle')}
        actions={
          <Button
            variant="secondary"
            icon={<RefreshCw size={15} className={reconciling ? 'animate-vela-spin' : ''} />}
            onClick={reconcile}
            disabled={reconciling}
          >
            {t('admin.reconcile')}
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {loading ? (
          Array.from({ length: 4 }, (_, index) => <Skeleton key={index} className="h-[132px]" />)
        ) : (
          <>
            <StatCard label={t('admin.servers')} value={formatNumber(servers.length)} icon={<Server size={20} />} />
            <StatCard
              label={t('nav.instances')}
              value={formatNumber(instances.length)}
              icon={<Network size={20} />}
              iconColor="var(--ok)"
              iconBg="var(--ok-soft)"
              delta={{
                value: t('dashboard.activeCount', {
                  count: instances.filter((entry) => entry.status === 'ACTIVE').length,
                }),
                positive: true,
              }}
            />
            <StatCard
              label={t('admin.customers')}
              value={formatNumber(users.length)}
              icon={<Users size={20} />}
              iconColor="var(--info)"
              iconBg="var(--info-soft)"
            />
            <StatCard
              label={t('admin.dedicatedIpv4')}
              value={t('admin.freeCount', { count: formatNumber(freeHostIps) })}
              icon={<HardDrive size={20} />}
              iconColor={freeHostIps === 0 ? 'var(--bad)' : 'var(--warn)'}
              iconBg={freeHostIps === 0 ? 'var(--bad-soft)' : 'var(--warn-soft)'}
              delta={{
                value: t('admin.inPool', { count: formatNumber(totalHostIps) }),
                positive: freeHostIps > 0,
              }}
            />
          </>
        )}
      </div>

      <div className="mt-5">
        <TabNav
          items={[
            { label: t('nav.adminServers'), to: paths.admin.servers },
            { label: t('nav.adminIpam'), to: paths.admin.ipam },
            { label: t('nav.adminInstances'), to: paths.admin.instances },
            { label: t('nav.adminUsers'), to: paths.admin.users },
            { label: t('nav.adminPlans'), to: paths.admin.plans },
            { label: t('nav.adminBilling'), to: paths.admin.billing },
            { label: t('nav.adminMail'), to: paths.admin.mail },
            { label: t('nav.adminSettings'), to: paths.admin.settings },
            { label: t('nav.adminAudit'), to: paths.admin.audit },
          ]}
        />
        <div className="mt-4">
          <Outlet />
        </div>
      </div>
    </AdminContext.Provider>
  );
}

/** Kleine Beschriftung fuer Detailfelder in den Administrationsseiten. */
export function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-[11.5px] text-t2">{label}</p>
      <p
        className={`mt-0.5 truncate text-[13px] text-t0 ${mono ? 'font-mono text-[11.5px]' : ''}`}
        title={value}
      >
        {value}
      </p>
    </div>
  );
}

export function AdminSkeleton({ children }: { children: ReactNode }) {
  const { loading } = useAdminData();
  return loading ? <Skeleton className="h-[320px]" /> : <>{children}</>;
}
